import {useState} from 'react'
import {Button, IconButton, Dialog} from '@primer/react'
import {RefSelector, type RefSelectorProps} from '@github-ui/ref-selector'
/* eslint-disable-next-line @github-ui/github-monorepo/no-use-app-payload --
   useAppPayload is deprecated. See
   https://thehub.github.com/epd/engineering/dev-practicals/frontend/react/data-router/migration/app-payload-deprecation/ */
import {useAppPayload} from '@github-ui/react-core/use-app-payload'
import {InlineMessage} from '@primer/react/experimental'
import {useQueryClient} from '@github-ui/react-query'
import {ArrowLeftIcon, TriangleDownIcon} from '@primer/octicons-react'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {useChangeBaseBranchMutation} from '../mutations/use-change-base-branch-mutation'
import {getBaseBranchText} from '../utils/branch-label'
import {useHeaderPageDataQueryKey} from '../page-data/loaders/use-header-page-data'
import type {PullRequestAppPayload} from '../page-data/payloads/pull-request-app'
import {useCommitsRoutePartialQueryKey} from '../routes/commits-route'
import {PullRequestBranchName} from './PullRequestBranchName'
import {PullRequestHeadBranchName, type PullRequestHeadBranchNameProps} from './PullRequestHeadBranchName'
import type {StackData} from '../page-data/payloads/header'
import styles from './PullRequestHeaderBranches.module.css'

export interface PullRequestHeaderBranchesProps extends PullRequestHeadBranchNameProps {
  baseBranch: string
  baseRepositoryDefaultBranch?: string
  baseRepositoryName: string
  canChangeBase?: boolean
  isEditing?: boolean
  setIsEditing?: (isEditing: boolean) => void
  stack?: StackData | null
}

/**
 * Renders UI for head and base branch + base branch picker and change base branch functionality.
 */
export function PullRequestHeaderBranches({
  baseBranch,
  baseRepositoryDefaultBranch = '',
  baseRepositoryName = '',
  baseRepositoryOwnerLogin = '',
  canChangeBase = false,
  headBranch,
  headRepositoryOwnerLogin = '',
  headRepositoryName = '',
  isInAdvisoryRepo,
  isEditing = false,
  setIsEditing,
  stack,
}: PullRequestHeaderBranchesProps) {
  const queryClient = useQueryClient()
  const {refListCacheKey} = useAppPayload<PullRequestAppPayload>()
  const [isConfirmingChangeBase, setIsConfirmingChangeBase] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')
  const [candidateBaseBranch, setCandidateBaseBranch] = useState('')

  const headerPageDataQueryKey = useHeaderPageDataQueryKey()
  const commitsRouteQueryKey = useCommitsRoutePartialQueryKey()
  const {mutate: mutateChangeBaseBranch} = useChangeBaseBranchMutation()

  const onSelectItem = (selectedBaseBranch: string) => {
    setIsConfirmingChangeBase(true)
    setCandidateBaseBranch(selectedBaseBranch)
  }

  const onCancelChangeBase = () => {
    setIsConfirmingChangeBase(false)
    setCandidateBaseBranch('')
    handleChangeBaseError('')
  }

  const handleChangeBaseError = (message: string) => {
    setIsLoading(false)
    setErrorMessage(message)
  }

  const showUpdatedHeader = isFeatureEnabled('updated_pull_request_header')

  const onConfirmChangeBase = () => {
    setIsLoading(true)
    mutateChangeBaseBranch(
      {newBaseBranch: candidateBaseBranch},
      {
        onError: error => handleChangeBaseError(error.message),
        onSuccess: async () => {
          try {
            await Promise.all([
              // We're using refetchQueries here instead of invalidateQueries in order to ensure that the
              // page data is updated *before* the confirmation dialog is closed, for a smoother overall UX.
              queryClient.refetchQueries({queryKey: headerPageDataQueryKey}, {throwOnError: true}),
              queryClient.refetchQueries({queryKey: commitsRouteQueryKey}, {throwOnError: true}),
            ])
            setIsLoading(false)
            // With updated header, base branch editing is unlinked from title editing state
            if (!showUpdatedHeader) setIsEditing?.(false)
            setIsConfirmingChangeBase(false)
          } catch {
            handleChangeBaseError('Unable to refetch commits. Please refresh the page.')
          }
        },
      },
    )
  }

  const baseBranchText = getBaseBranchText(baseBranch, baseRepositoryOwnerLogin, headRepositoryOwnerLogin)

  const refSelectorProps: RefSelectorProps = {
    cacheKey: refListCacheKey,
    canCreate: false,
    closeOnSelect: true,
    currentCommitish: baseBranch,
    defaultBranch: baseRepositoryDefaultBranch,
    hideShowAll: true,
    owner: baseRepositoryOwnerLogin,
    repo: baseRepositoryName,
    types: ['branch'],
    onSelectItem,
  }

  const isStack = !!stack

  return (
    <>
      <div className="d-flex flex-items-center gap-1">
        {showUpdatedHeader ? (
          <>
            <span className={styles.baseBranchGroup}>
              <span className="sr-only">Base branch:</span>
              <PullRequestBranchName
                branch={baseBranch}
                branchText={baseBranchText}
                repositoryName={baseRepositoryName}
                repositoryOwner={baseRepositoryOwnerLogin}
                button={
                  canChangeBase &&
                  (isStack ? (
                    <IconButton
                      aria-disabled
                      aria-label="Change base branch"
                      description="Cannot change base branch of stacked pull request"
                      icon={TriangleDownIcon}
                      size="small"
                      variant="invisible"
                    />
                  ) : (
                    <RefSelector
                      {...refSelectorProps}
                      renderAnchor={props => (
                        <IconButton
                          {...props}
                          icon={TriangleDownIcon}
                          variant="invisible"
                          size="small"
                          aria-label="Change base branch"
                        />
                      )}
                    />
                  ))
                }
              />
              <ArrowLeftIcon size={16} className="fgColor-muted" />
            </span>
            <span className="sr-only">Head branch:</span>
            <PullRequestHeadBranchName
              baseRepositoryOwnerLogin={baseRepositoryOwnerLogin}
              headBranch={headBranch}
              headRepositoryOwnerLogin={headRepositoryOwnerLogin}
              headRepositoryName={headRepositoryName}
              isInAdvisoryRepo={isInAdvisoryRepo}
            />
          </>
        ) : (
          <>
            {isEditing && canChangeBase && !isStack ? (
              <RefSelector {...refSelectorProps} />
            ) : (
              <PullRequestBranchName
                branch={baseBranch}
                branchText={baseBranchText}
                repositoryName={baseRepositoryName}
                repositoryOwner={baseRepositoryOwnerLogin}
              />
            )}
            <span>from </span>
            <PullRequestHeadBranchName
              baseRepositoryOwnerLogin={baseRepositoryOwnerLogin}
              headBranch={headBranch}
              headRepositoryOwnerLogin={headRepositoryOwnerLogin}
              headRepositoryName={headRepositoryName}
              isInAdvisoryRepo={isInAdvisoryRepo}
            />
          </>
        )}
      </div>

      {isConfirmingChangeBase && (
        <Dialog
          onClose={onCancelChangeBase}
          title="Are you sure you want to change the base?"
          width="large"
          renderFooter={() => (
            <Dialog.Footer>
              <Button onClick={onCancelChangeBase} aria-disabled={isLoading ? 'true' : undefined}>
                Cancel
              </Button>
              <Button
                aria-disabled={isLoading ? 'true' : undefined}
                loading={isLoading}
                onClick={onConfirmChangeBase}
                variant="primary"
              >
                Change base
              </Button>
            </Dialog.Footer>
          )}
        >
          <span>
            Some commits from the old base branch may be removed from the timeline, and old review comments may become
            outdated.
          </span>
          {errorMessage && (
            <InlineMessage className="mt-2" variant="critical">
              {errorMessage}
            </InlineMessage>
          )}
          {/* Loading message accessible to screen readers only. See https://primer.style/components/button#button-loading-state */}
          <span className="sr-only" aria-live="polite">
            {isLoading ? 'Base branch update in progress.' : ''}
          </span>
        </Dialog>
      )}
    </>
  )
}
